import React from "react";
import { useTranslation } from "react-i18next";
import { DownloadSimple } from "@phosphor-icons/react";
import { exportTooltip } from "../workflow/workflowState";
import { PrimaryCta } from "./PrimaryCta";
import { ChecklistRow } from "./FilingChecklistRow";
import { CountryReturnsChecklistRow } from "./CountryReturnsChecklistRow";
import { SubmissionRecordPanel } from "./SubmissionRecordPanel";

// v0.6 P2 refactor — filing checklist extracted from
// Step6FreezeExport.jsx (500-line cap). Behaviour unchanged; the
// step state/handlers are passed in as props.
export const FilingChecklist = ({
  prereqsReady, prereqsTotal, signedOff, calc,
  signoffPrereqs, setActiveStep, frozen, filingRef,
  handleCopyReference, copied, freezeError, handleFreeze,
  freezing, hasOssRows, lint, setLintModalOpen,
  fcSummary, allowed, exportBlockTip, xlsxPrereqs,
  exporting, exportError, handleExport, hasIossRows, iossPrereqs,
  iossExporting, iossExportError, handleIossExport, ossPrereqs, ossExporting, ossExportError,
  handleOssExport, hasNonUnionOssRows, nonUnionOssExporting, nonUnionOssExportError, handleNonUnionOssExport,
  handleSupersede, superseded, freezeBlockedReason = null, supersedeBlockedReason = null,
}) => {
  const { t } = useTranslation();
  return (
      <div
        data-testid="filing-checklist"
        className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl p-4 space-y-2"
      >
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">
            {t("ui.dashboard.filing_workflow_title")}
          </div>
          <div
            data-testid="filing-readiness-status"
            className={`tabular-nums text-[11px] tracking-normal ${
              prereqsReady === prereqsTotal
                ? "text-emerald-700 dark:text-emerald-300"
                : "text-amber-700 dark:text-amber-300"
            }`}
          >
            {prereqsReady}/{prereqsTotal} steps complete
          </div>
        </div>
        <ChecklistRow
          done={signedOff}
          label="1 · Sign-off complete"
          sublabel={signedOff
            ? t("ui.dashboard.signed_by", { name: calc?.signed_off_by || t("ui.dashboard.signed_by_fallback", { defaultValue: "operator" }), defaultValue: `Signed by ${calc?.signed_off_by || t("ui.dashboard.signed_by_fallback", { defaultValue: "operator" })}` })
            : t("ui.dashboard.signoff_sublabel", { defaultValue: "You confirm the calculation is correct and ready to file." })}
          prerequisites={signoffPrereqs}
          onGoTo={(s) => setActiveStep(s)}
          action={!signedOff && (
            <button type="button" data-testid="step-6-goto-signoff"
              onClick={() => setActiveStep(5)}
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold text-[10px] px-3 py-2 rounded-xl">
              {t("ui.dashboard.signoff_cta", { defaultValue: "Sign off →" })}
            </button>
          )}
        />
        <ChecklistRow
          done={frozen && !!filingRef}
          label="2 · Lock the calculation"
          sublabel={frozen && filingRef
            ? (
              <span>
                {t("ui.dashboard.reference_label", { defaultValue: "Reference:" })} <span className="text-amber-700 dark:text-amber-300">{filingRef}</span>
                {" "}
                <button
                  type="button"
                  data-testid="copy-filing-reference"
                  onClick={handleCopyReference}
                  className="ml-2 text-teal-700 dark:text-teal-300 hover:text-teal-700 dark:hover:text-teal-300 underline decoration-dotted text-[10px] tracking-normal"
                  title={t("ui.dashboard.copy_reference_title", { defaultValue: "Copy filing reference + audit URL (markdown link)" })}
                >{copied
                  ? t("ui.dashboard.copy_reference_done", { defaultValue: "Copied!" })
                  : t("ui.dashboard.copy_reference", { defaultValue: "copy reference + audit URL" })}</button>
              </span>
            )
            : signedOff
              ? (freezeBlockedReason ? freezeBlockedReason : freezeError
                  ? t("ui.dashboard.could_not_lock", { error: freezeError, defaultValue: `Could not lock — ${freezeError}` })
                  : t("ui.dashboard.lock_sublabel", { defaultValue: "Locks the calculation forever and assigns its filing reference." }))
              : t("ui.dashboard.lock_sublabel_pending", { defaultValue: "Available after sign-off." })}
          action={
            (signedOff && !frozen && (
              <button
                type="button"
                data-testid="step-6-freeze-action"
                data-resolution-class="self-resolve"
                data-resolution-endpoint="POST /api/runs/calc/{calc_id}/freeze"
                onClick={handleFreeze}
                disabled={freezing || !!freezeBlockedReason}
                title={freezeBlockedReason || undefined}
                className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-[10px] px-3 py-2 rounded-xl"
              >
                {freezing ? t("ui.dashboard.locking", { defaultValue: "Locking…" }) : t("ui.dashboard.lock_cta", { defaultValue: "❄ Lock for filing →" })}
              </button>
            ))
            || (frozen && !superseded && (
              <span className="flex flex-col items-end gap-1">
              <button
                type="button"
                data-testid="step-6-supersede-action"
                data-resolution-class="self-resolve"
                data-resolution-endpoint="POST /api/runs/calc/{old_id}/supersede"
                onClick={handleSupersede}
                disabled={!!supersedeBlockedReason}
                title={supersedeBlockedReason || t("ui.dashboard.supersede_title", { defaultValue: "Redo this frozen period as a new, tracked calculation" })}
                className="border-2 border-amber-500 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40 disabled:opacity-40 disabled:cursor-not-allowed font-semibold text-[10px] px-3 py-2 rounded-xl"
              >
                {t("ui.dashboard.supersede_button", { defaultValue: "↺ Supersede this filing →" })}
              </button>
              {supersedeBlockedReason && (
                <span data-testid="step-6-supersede-blocked" className="max-w-xs text-right text-xs text-amber-700 dark:text-amber-300">
                  {supersedeBlockedReason}
                </span>
              )}
              </span>
            ))
          }
        />
        {/* v0.6.12 — sits directly under "Lock the calculation" because that
            is the narrative: locking finalises the figures HERE, and this is
            where the operator records that the return actually reached the
            authority. Renders nothing until the calc is frozen. */}
        <SubmissionRecordPanel calc={calc} />
        <ChecklistRow
          done={!hasOssRows || lint.ok}
          label="3 · OSS rate review"
          sublabel={!hasOssRows
            ? t("ui.dashboard.oss_lint_na", { defaultValue: "Not applicable — no OSS rows in this calculation." })
            : lint.ok
              ? t("ui.dashboard.oss_lint_ok", { defaultValue: "Every OSS row matches its destination country's official VAT rate." })
              : t("ui.dashboard.oss_lint_bad", { n: lint.offender_count, defaultValue: `${lint.offender_count} row(s) have a rate that doesn't match the destination country's official table — review before exporting OSS XML.` })}
          action={hasOssRows && !lint.ok && (
            <button type="button"
              data-testid="oss-lint-review-button"
              onClick={() => setLintModalOpen(true)}
              className="bg-amber-600 hover:bg-amber-400 text-white font-semibold text-[10px] px-3 py-2 rounded-xl">
              {t("ui.dashboard.oss_lint_review", { n: lint.offender_count, defaultValue: `Review ${lint.offender_count} row(s) →` })}
            </button>
          )}
        />
        {fcSummary && (
          <ChecklistRow
            done={fcSummary.reconcile_ok}
            label={t("ui.dashboard.fc_label", { defaultValue: "Fulfilment-centre stock movements" })}
            sublabel={
              `EC Sales List entries: ${fcSummary.esl_group_count} dispatch group(s) (€${fcSummary.esl_total_cost_price}) · Goods received in ${fcSummary.acquisition_country_count} country(ies) · Sends-vs-receives match: ${fcSummary.reconcile_ok ? "✓ net VAT impact €0" : "⚠ imbalance"}`
            }
            data-testid="fc-transfer-summary-row"
          />
        )}
        <CountryReturnsChecklistRow calcId={calc?.id} position={4} />
        <ChecklistRow
          done={false}
          label="5 · Download filing spreadsheet (XLSX)"
          // v0.6.8 fix — same fix as the IOSS/OSS/Non-Union-OSS rows: a
          // failed XLSX export set `exportError`, but it only ever
          // rendered in a shared box below ReconciliationPanel +
          // FilingPackagePanel — off-screen from this row. That box
          // still shows the fuller breach-link detail when present;
          // this just makes sure the plain error is visible immediately
          // instead of requiring a scroll to notice anything happened.
          sublabel={exportError
            ? t("ui.dashboard.could_not_export", { error: exportError, defaultValue: `Could not export — ${exportError}` })
            : allowed
              ? "Filing-ready Excel workbook — carries the locked filing reference."
              : (exportBlockTip || exportTooltip())}
          prerequisites={xlsxPrereqs}
          onGoTo={(s) => setActiveStep(s)}
          action={
            <PrimaryCta
              testid="export-xlsx-button"
              disabled={!allowed}
              busy={exporting}
              title={!allowed ? (exportBlockTip || exportTooltip()) : undefined}
              onClick={handleExport}
            >
              <DownloadSimple size={14} weight="bold" />
              {exporting ? t("ui.dashboard.downloading", { defaultValue: "Downloading…" }) : t("ui.dashboard.download_xlsx", { defaultValue: "Download XLSX" })}
            </PrimaryCta>
          }
        />
        {hasIossRows && (
          <ChecklistRow
            done={false}
            label="6 · Download IOSS XML (EU import scheme)"
            // v0.6.8 fix — this used to only ever show the static
            // sublabel; a failed export (e.g. missing MSI country) set
            // an error that rendered in a shared box far below the fold
            // (after ReconciliationPanel + FilingPackagePanel), reading
            // as the button silently doing nothing. Surface it right
            // here instead, same pattern the Lock row already uses for
            // freezeError.
            sublabel={iossExportError
              ? t("ui.dashboard.could_not_export", { error: iossExportError, defaultValue: `Could not export — ${iossExportError}` })
              : frozen
                ? t("ui.dashboard.ioss_sublabel_ready", { defaultValue: "IOSS XML file — hand to your tax representative for the IOSS portal in your registered EU country." })
                : t("ui.dashboard.export_oss_sublabel_locked", { defaultValue: "Available after locking — same audit trail as the spreadsheet." })}
            prerequisites={iossPrereqs}
            onGoTo={(s) => setActiveStep(s)}
            action={
              <PrimaryCta
                testid="export-ioss-xml-button"
                disabled={!frozen}
                busy={iossExporting}
                title={!frozen ? t("ui.dashboard.lock_first", { defaultValue: "Lock the calculation first" }) : undefined}
                onClick={handleIossExport}
              >
                <DownloadSimple size={14} weight="bold" />
                {iossExporting ? t("ui.dashboard.downloading", { defaultValue: "Downloading…" }) : t("ui.dashboard.download_ioss_xml", { defaultValue: "Download IOSS XML" })}
              </PrimaryCta>
            }
          />
        )}
        {hasOssRows && (
          <ChecklistRow
            done={false}
            label={`${hasIossRows ? "7" : "6"} · Download OSS XML (EU one-stop-shop)`}
            sublabel={ossExportError
              ? t("ui.dashboard.could_not_export", { error: ossExportError, defaultValue: `Could not export — ${ossExportError}` })
              : !lint.ok
                ? t("ui.dashboard.oss_blocked", { n: lint.offender_count, defaultValue: `Blocked — ${lint.offender_count} row(s) have a rate the destination country doesn't recognise.` })
                : frozen
                  ? t("ui.dashboard.export_oss_sublabel_ready")
                  : t("ui.dashboard.export_oss_sublabel_locked")}
            prerequisites={ossPrereqs}
            onGoTo={(s) => setActiveStep(s)}
            action={
              <PrimaryCta
                testid="export-oss-xml-button"
                disabled={!frozen || !lint.ok}
                busy={ossExporting}
                title={!frozen ? t("ui.dashboard.lock_first", { defaultValue: "Lock the calculation first" }) : (!lint.ok ? t("ui.dashboard.oss_lint_first", { defaultValue: "Resolve the OSS rate review first" }) : undefined)}
                onClick={handleOssExport}
              >
                <DownloadSimple size={14} weight="bold" />
                {ossExporting ? t("ui.dashboard.downloading", { defaultValue: "Downloading…" }) : t("ui.dashboard.download_oss_xml", { defaultValue: "Download OSS XML" })}
              </PrimaryCta>
            }
          />
        )}
        {/* v0.5.0i+21 — Non-Union OSS XML row. Rendered only when the
            calc actually contains EU_NON_UNION_OSS rows (Art 58 rule
            routed UK / non-EU sellers of digital services to EU
            consumers). Step number cascades after IOSS + Union OSS. */}
        {hasNonUnionOssRows && (
          <ChecklistRow
            done={false}
            label={`${
              6 + (hasIossRows ? 1 : 0) + (hasOssRows ? 1 : 0)
            } · Download Non-Union OSS XML (Art 58 · non-EU sellers)`}
            sublabel={nonUnionOssExportError
              ? t("ui.dashboard.could_not_export", { error: nonUnionOssExportError, defaultValue: `Could not export — ${nonUnionOssExportError}` })
              : frozen
                ? t("ui.dashboard.non_union_sublabel_ready", { defaultValue: "Non-Union OSS XML — file with your chosen MSI (Member State of Identification). Set the MSI in Settings → Company → VAT registrations." })
                : t("ui.dashboard.export_oss_sublabel_locked", { defaultValue: "Available after locking — same audit trail as the spreadsheet." })}
            prerequisites={ossPrereqs}
            onGoTo={(s) => setActiveStep(s)}
            action={
              <PrimaryCta
                testid="export-non-union-oss-xml-button"
                disabled={!frozen}
                busy={nonUnionOssExporting}
                title={!frozen ? t("ui.dashboard.lock_first", { defaultValue: "Lock the calculation first" }) : undefined}
                onClick={handleNonUnionOssExport}
              >
                <DownloadSimple size={14} weight="bold" />
                {nonUnionOssExporting ? t("ui.dashboard.downloading", { defaultValue: "Downloading…" }) : t("ui.dashboard.download_non_union_xml", { defaultValue: "Download Non-Union OSS XML" })}
              </PrimaryCta>
            }
          />
        )}
      </div>
  );
};
